import { Box, Center, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import {
  LEFT_CURSOR_URL,
  RIGHT_CURSOR_URL,
} from "../reader/components/horizontalReading/icons";
import { UP_CURSOR_URL, DOWN_CURSOR_URL } from "./cursors";
import { Page, Release } from "../../types";
import { ReleaseInfoActions } from "../../components/slideInMenu/releaseInfo/ReleaseInfoActions";
import { useReleaseInfoPaywall } from "../../components/slideInMenu/releaseInfo/hooks/useReleaseInfoPaywall";
import { useSlideInFunnel } from "../../components/slideInMenu/hooks/useSlideInFunnel";
import { useFunnel } from "../../components/funnel/hooks/useFunnel";
import {
  EventContextArea,
  FunnelContextArea,
} from "../../components/funnel/types";
import { FeedOnboardingOverlay } from "./FeedOnboardingOverlay";
import { useFeedExpand } from "./feedExpandContext";

const NAV_ZONE_WIDTH = 0.35;
// Pinch-out (spread) → expand to fullscreen preview when current spread is
// >1.4× the initial spread between two pointers. Symmetric counterpart to
// the pinch-in dismissal in FeedFullscreenPreview.
const PINCH_EXPAND_RATIO = 1.4;
// How many cards we *visually* show stacked behind the active one.
const DECK_DEPTH = 2;
// How many we actually MOUNT (a small window around the active page) so a
// long release doesn't fire hundreds of <Image> requests at once.
const RENDER_AHEAD = 3;
const RENDER_BEHIND = 2;
// Stack, horizontal: active page sits centered; prev/next sit BEHIND it,
// translated so a recognizable strip of the page peeks at the slot edges.
// Once the active image reports its rendered width, peek cards translate by
// `width * PER_CARD_PEEK_RATIO` PX so the peek is keyed to the page art (not
// the slot, which is much wider for portrait pages on desktop).
// PER_CARD_X_PCT is the slot-%-based fallback used until the first measure.
const PER_CARD_X_PCT = 78;
const PER_CARD_PEEK_RATIO = 0.85;
const PER_CARD_SCALE = 0.06;

interface FeedPagesViewerProps {
  releaseUuid: string;
  pages: Page[];
  isPaywalled: boolean;
  isLoading: boolean;
  // Optional release-specific cover URL (rendered as deck card 0).
  coverImageUrl?: string;
  // When false (peek slot), the deck still renders but click/cursor nav is
  // disabled — the user must vertical-swipe to make this slot active first.
  isInteractive: boolean;
  // 'l' (left-to-right, default) or 'r' (right-to-left). Flips the deck peek
  // direction, swipe semantics, cursor zones, and arrow-key bindings so RTL
  // titles read naturally — the "next" card peeks on the LEFT edge.
  readingDirection?: string;
  // Vertical-feed nav: invoked when the user clicks the center zone (top
  // half = retreat to previous comic, bottom half = advance to next). Only
  // fire / show their cursor when the corresponding direction has somewhere
  // to go.
  onAdvanceFeed?: () => void;
  onRetreatFeed?: () => void;
  hasFeedPrev?: boolean;
  hasFeedNext?: boolean;
  // Full release entity — used by the inline paywall card to render the
  // subscribe / buy CTA via ReleaseInfoActions.
  release?: Release;
  // Fires whenever the active in-comic page changes so the parent can keep
  // a reference (used to deep-link the "Read full" button to the user's
  // current page in the full reader). null while on the cover slot.
  onActivePageChange?: (pageOrder: number | null) => void;
  // Optional slot index to start at — 0 = cover, 1+ = page positions in the
  // deck. The fullscreen preview passes the underlying viewer's current
  // activePage so the user lands on the page they were already reading.
  initialSlotIndex?: number;
}

type NavZone = "prev" | "next" | "center";

export function FeedPagesViewer({
  releaseUuid,
  pages,
  isPaywalled,
  isLoading,
  coverImageUrl,
  isInteractive,
  readingDirection,
  onAdvanceFeed,
  onRetreatFeed,
  hasFeedPrev = false,
  hasFeedNext = false,
  release,
  onActivePageChange,
  initialSlotIndex,
}: FeedPagesViewerProps) {
  const isMobile = useMobileBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const isRtl = readingDirection === "r";
  // Direction multiplier: +1 LTR (next peeks right), -1 RTL (next peeks left).
  const dirSign = isRtl ? -1 : 1;

  const [activePage, setActivePage] = useState(initialSlotIndex ?? 0);

  // Live-measured rendered width of the active image. Peek cards key their
  // translate to this so the peek-strip is a fraction of the page art, not
  // the slot. null until the first onLoad/measure fires.
  const [activeImageWidthPx, setActiveImageWidthPx] = useState<number | null>(null);

  // Reset to first page whenever the active release CHANGES — compare
  // against the mount-time uuid rather than a "did we run yet" flag so
  // StrictMode's double-invoke (dev) doesn't fire a phantom reset that
  // clobbers initialSlotIndex on first paint.
  const initialReleaseUuid = useRef(releaseUuid);
  useEffect(() => {
    if (releaseUuid !== initialReleaseUuid.current) {
      setActivePage(0);
    }
  }, [releaseUuid]);

  // Reset measured width whenever the active slot changes (cover ↔ page,
  // page-to-page) — the next image may have a different intrinsic aspect.
  useEffect(() => {
    setActiveImageWidthPx(null);
  }, [releaseUuid, activePage]);

  const pageCount = pages.length;

  const slots = useMemo(() => {
    const out: Slot[] = [];
    if (coverImageUrl) {
      out.push({ kind: "cover" as const, url: coverImageUrl });
    }
    for (const p of pages) {
      out.push({ kind: "page" as const, page: p });
    }
    return out;
  }, [coverImageUrl, pages]);

  // Surface the current page to the parent so the "Read full" button can
  // deep-link into the full reader at the same page.
  useEffect(() => {
    const slot = slots[activePage];
    onActivePageChange?.(slot?.kind === "page" ? slot.page.order : null);
  }, [activePage, slots, onActivePageChange]);

  const goNext = useCallback(() => {
    setActivePage((p) => Math.min(p + 1, slots.length - 1));
  }, [slots.length]);
  const goPrev = useCallback(() => {
    setActivePage((p) => Math.max(p - 1, 0));
  }, []);

  const getZone = useCallback((clientX: number): NavZone => {
    const el = containerRef.current;
    if (!el) return "center";
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    if (x < rect.width * NAV_ZONE_WIDTH) return "prev";
    if (x > rect.width * (1 - NAV_ZONE_WIDTH)) return "next";
    return "center";
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isInteractive) return;
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"]')) return;
      // Drag-tail clicks (the click that fires after a mouse drag) should
      // not trigger zone-based nav — the drag already handled it.
      const lastDrag = (window as { __feedJustDragged?: number })
        .__feedJustDragged;
      if (lastDrag && Date.now() - lastDrag < 300) return;
      const el = containerRef.current;
      if (!el) return;
      const zone = getZone(e.clientX);
      if (zone === "prev" || zone === "next") {
        // Spatial zones: left zone always advances toward "earlier visually,"
        // right zone toward "later visually." For LTR that maps to prev/next
        // in reading order; RTL flips it.
        const advanceReadingOrder = isRtl ? zone === "prev" : zone === "next";
        if (advanceReadingOrder) goNext();
        else goPrev();
      } else {
        // Center zone — split top/bottom for vertical feed nav. Only fire
        // when the direction has somewhere to go.
        const rect = el.getBoundingClientRect();
        const y = e.clientY - rect.top;
        if (y < rect.height / 2) {
          if (hasFeedPrev) onRetreatFeed?.();
        } else {
          if (hasFeedNext) onAdvanceFeed?.();
        }
      }
    },
    [
      isInteractive,
      getZone,
      goNext,
      goPrev,
      onAdvanceFeed,
      onRetreatFeed,
      hasFeedPrev,
      hasFeedNext,
      isRtl,
    ],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = containerRef.current;
      if (!el) return;
      if (!isInteractive) {
        el.style.cursor = "";
        return;
      }
      const zone = getZone(e.clientX);
      const hasPrev = activePage > 0;
      const hasNext = activePage < slots.length - 1;
      let cursor = "";
      if (zone === "prev" || zone === "next") {
        // Cursor reflects spatial direction (left arrow on left zone, right
        // arrow on right zone). Whether that direction has anywhere to go
        // depends on reading order: in RTL, left=next so we check hasNext.
        const advanceReadingOrder = isRtl ? zone === "prev" : zone === "next";
        const canGo = advanceReadingOrder ? hasNext : hasPrev;
        if (canGo) cursor = zone === "prev" ? LEFT_CURSOR_URL : RIGHT_CURSOR_URL;
      } else if (zone === "center") {
        const rect = el.getBoundingClientRect();
        const y = e.clientY - rect.top;
        if (y < rect.height / 2 && hasFeedPrev) cursor = UP_CURSOR_URL;
        else if (y >= rect.height / 2 && hasFeedNext) cursor = DOWN_CURSOR_URL;
      }
      el.style.cursor = cursor;
    },
    [
      isInteractive,
      getZone,
      activePage,
      slots.length,
      hasFeedPrev,
      hasFeedNext,
      isRtl,
    ],
  );

  // Arrow keys for horizontal page nav (vertical nav is handled by SuggestionFeed's
  // window-level listener). Only the active slot responds. RTL flips so the
  // left arrow advances reading order — same as the reader convention.
  useEffect(() => {
    if (!isInteractive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (isRtl) goNext();
        else goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (isRtl) goPrev();
        else goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isInteractive, goPrev, goNext, isRtl]);

  // Pinch-out detection. We track up to two active pointers and watch the
  // distance between them; once it grows past PINCH_EXPAND_RATIO × the
  // initial spread, hand off to the fullscreen preview. Only the active
  // slot listens — non-active decks shouldn't capture the gesture.
  const { open: openExpand } = useFeedExpand();
  const pinchPointers = useRef<
    Map<number, { x: number; y: number }>
  >(new Map());
  const pinchInitialSpread = useRef<number | null>(null);
  const onPinchPointerDown = useCallback((e: React.PointerEvent) => {
    if (!isInteractive) return;
    pinchPointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });
    if (pinchPointers.current.size === 2) {
      const [a, b] = Array.from(pinchPointers.current.values());
      pinchInitialSpread.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  }, [isInteractive]);
  const onPinchPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isInteractive) return;
      if (!pinchPointers.current.has(e.pointerId)) return;
      pinchPointers.current.set(e.pointerId, {
        x: e.clientX,
        y: e.clientY,
      });
      if (
        pinchPointers.current.size === 2 &&
        pinchInitialSpread.current !== null &&
        pinchInitialSpread.current > 0
      ) {
        const [a, b] = Array.from(pinchPointers.current.values());
        const spread = Math.hypot(a.x - b.x, a.y - b.y);
        if (spread / pinchInitialSpread.current > PINCH_EXPAND_RATIO) {
          pinchPointers.current.clear();
          pinchInitialSpread.current = null;
          openExpand({
            releaseUuid,
            readingDirection,
            // Hand the user's current slot index to the fullscreen so the
            // preview opens on the same page they were already reading.
            initialSlotIndex: activePage,
          });
        }
      }
    },
    [isInteractive, openExpand, releaseUuid, readingDirection, activePage],
  );
  const onPinchPointerEnd = useCallback((e: React.PointerEvent) => {
    pinchPointers.current.delete(e.pointerId);
    if (pinchPointers.current.size < 2) {
      pinchInitialSpread.current = null;
    }
  }, []);

  // Horizontal touch swipe — dispatched as a custom event from SuggestionFeed
  // (which owns the touch handler so vertical/horizontal direction can be
  // disambiguated by dominant axis). The dispatched detail is the raw swipe
  // direction ("left" or "right"); the viewer maps that to reading-order
  // next/prev based on its own reading direction. Only the active slot
  // responds.
  useEffect(() => {
    if (!isInteractive) return;
    const onPageNav = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Standard swipe convention: drag-left advances visually-rightward
      // content. In LTR that's "next page"; in RTL the same drag advances
      // *backward* in reading order.
      if (detail === "left") {
        if (isRtl) goPrev();
        else goNext();
      } else if (detail === "right") {
        if (isRtl) goNext();
        else goPrev();
      }
    };
    window.addEventListener("feed-page-nav", onPageNav);
    return () => window.removeEventListener("feed-page-nav", onPageNav);
  }, [isInteractive, goNext, goPrev, isRtl]);

  if (isLoading) {
    return (
      <Center h="100%" w="100%">
        <Spinner color="orange.400" size="lg" />
      </Center>
    );
  }

  if (!pageCount && !isPaywalled) {
    return (
      <Center h="100%" w="100%">
        <Text color="white" opacity={0.6}>
          No preview available
        </Text>
      </Center>
    );
  }

  return (
    <Flex
      ref={containerRef}
      position="relative"
      w="100%"
      h="100%"
      alignItems="center"
      justifyContent="center"
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onPointerDown={onPinchPointerDown}
      onPointerMove={onPinchPointerMove}
      onPointerUp={onPinchPointerEnd}
      onPointerCancel={onPinchPointerEnd}
      sx={{ userSelect: "none" }}
    >
      {slots.map((slot, i) => {
        const offset = i - activePage;
        // Only mount cards within the visible deck window.
        if (offset < -RENDER_BEHIND || offset > RENDER_AHEAD) return null;
        const key =
          slot.kind === "page" ? `page-${slot.page.id}` : "cover";
        return (
          <DeckCard
            key={key}
            slot={slot}
            offset={offset}
            isMobile={isMobile}
            release={release}
            dirSign={dirSign}
            activeImageWidthPx={activeImageWidthPx}
            onActiveImageMeasure={
              offset === 0 ? setActiveImageWidthPx : undefined
            }
          />
        );
      })}
      {/* "Swipe to read" hint — only on the active comic's starting slot.
          The user's first page-flip naturally hides it via activePage > 0,
          so no separate dismissal/state is needed. */}
      {isInteractive && activePage === 0 ? (
        <FeedOnboardingOverlay readingDirection={readingDirection} />
      ) : null}
    </Flex>
  );
}

type Slot = { kind: "cover"; url: string } | { kind: "page"; page: Page };

interface DeckCardProps {
  slot: Slot;
  offset: number;
  isMobile: boolean;
  release?: Release;
  // +1 LTR (next peeks right), -1 RTL (next peeks left).
  dirSign: number;
  // Live-measured rendered width of the active page image. Peek cards
  // translate by an absolute px amount keyed off this so a portrait page in
  // a wide slot still shows a meaningful peek strip.
  activeImageWidthPx: number | null;
  // Only the active card receives this; fires on image load + on resize.
  onActiveImageMeasure?: (px: number) => void;
}

function DeckCard({
  slot,
  offset,
  isMobile,
  release,
  dirSign,
  activeImageWidthPx,
  onActiveImageMeasure,
}: DeckCardProps) {
  const imgRef = useRef<HTMLImageElement>(null);

  // Measure the active image's rendered width once it lays out, and again
  // on resize. Only the active card carries the callback.
  useEffect(() => {
    if (offset !== 0 || !onActiveImageMeasure) return;
    const img = imgRef.current;
    if (!img) return;
    const measure = () => {
      const w = img.getBoundingClientRect().width;
      if (w > 0) onActiveImageMeasure(w);
    };
    if (img.complete) measure();
    img.addEventListener("load", measure);
    const ro = new ResizeObserver(measure);
    ro.observe(img);
    return () => {
      img.removeEventListener("load", measure);
      ro.disconnect();
    };
  }, [offset, onActiveImageMeasure]);

  let transform = "translate(0, 0) scale(1) rotate(0deg)";
  let opacity = 1;
  let zIndex = 100;
  let pointerEvents: "auto" | "none" = "auto";
  let brightness = 1;

  if (offset === 0) {
    // Active page — full size, front and center.
    transform = "translate(0, 0) scale(1)";
    opacity = 1;
    zIndex = 500;
    pointerEvents = "auto";
  } else {
    const depth = Math.min(Math.abs(offset), DECK_DEPTH);
    const beyondDeck = Math.abs(offset) > DECK_DEPTH;
    // Negative offset = previous page; positive = next page. dirSign flips
    // visual side for RTL so the "next" card peeks on the LEFT.
    const direction = (offset < 0 ? -1 : 1) * dirSign;
    const scale = 1 - depth * PER_CARD_SCALE;
    // Prefer absolute px keyed off the active page image; fall back to the
    // slot-% translate while we wait for the first measure.
    const xExpr =
      activeImageWidthPx !== null
        ? `${direction * depth * activeImageWidthPx * PER_CARD_PEEK_RATIO}px`
        : `${direction * depth * PER_CARD_X_PCT}%`;
    transform = `translate(${xExpr}, 0) scale(${scale})`;
    opacity = beyondDeck ? 0 : Math.max(0.1, 1 - depth * 0.25);
    brightness = Math.max(0.55, 1 - depth * 0.18);
    zIndex = 500 - depth;
    pointerEvents = "none";
  }

  return (
    <Box
      position="absolute"
      inset={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      transform={transform}
      opacity={opacity}
      zIndex={zIndex}
      pointerEvents={pointerEvents}
      transition="transform 280ms cubic-bezier(.2,.9,.3,1.05), filter 220ms ease-out"
      sx={{
        willChange: "transform, filter",
        filter: brightness < 1 ? `brightness(${brightness})` : undefined,
      }}
    >
      {slot.kind === "page" ? (
        // Backend serves a small thumbnail URL for blocked pages. Scale it
        // up to fill the slot's height, keeping the image's aspect ratio so
        // it doesn't distort. Width auto-computes from the image's aspect.
        <Image
          ref={imgRef}
          src={
            isMobile
              ? slot.page.mobile_image_url
              : slot.page.desktop_image_url
          }
          objectFit="contain"
          h={slot.page.display_paid_access_block ? "100%" : undefined}
          w={slot.page.display_paid_access_block ? "auto" : undefined}
          maxH="100%"
          maxW="100%"
          draggable={false}
          alt={
            slot.page.display_paid_access_block
              ? "Locked page"
              : `Page ${slot.page.order}`
          }
          // Active page wins the HTTP/2 priority race over the peek copies
          // that mount in the same render — without this, smaller peek
          // images sometimes paint first.
          fetchPriority={offset === 0 ? "high" : "low"}
          loading="eager"
          decoding={offset === 0 ? "sync" : "async"}
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.5)"
          borderRadius="4px"
          sx={
            slot.page.display_paid_access_block
              ? { filter: "blur(12px) grayscale(20%)" }
              : undefined
          }
        />
      ) : (
        <Image
          ref={imgRef}
          src={slot.url}
          objectFit="contain"
          maxH="100%"
          maxW="100%"
          draggable={false}
          alt="Cover"
          fetchPriority={offset === 0 ? "high" : "low"}
          loading="eager"
          decoding={offset === 0 ? "sync" : "async"}
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.5)"
          borderRadius="4px"
        />
      )}
      {/* Paywall overlay — only on the *active* blocked page, not the
          backing-deck copies (those are scaled / dimmed already). */}
      {offset === 0 &&
      slot.kind === "page" &&
      slot.page.display_paid_access_block &&
      release ? (
        <Flex
          position="absolute"
          inset={0}
          alignItems="center"
          justifyContent="center"
          bg="rgba(0, 0, 0, 0.55)"
          backdropFilter="blur(2px)"
          pointerEvents="auto"
        >
          <UnlockCard release={release} />
        </Flex>
      ) : null}
    </Box>
  );
}

function UnlockCard({ release }: { release: Release }) {
  // Embed the full funnel inline — same pattern as the release-info slide-in
  // menu (auth → payment → congrats all happen here, no modal). The paywall
  // CTA is a ReleaseInfoActions; once the user clicks Subscribe/Buy, the
  // local funnel state activates and we render funnelBody/funnelFooter
  // instead of the CTA. Auth chaining is handled by useReleaseInfoPaywall +
  // useFunnel, so guests get the login/signup step automatically.
  const { funnelConfig, setFunnelConfig } = useSlideInFunnel({});
  const openPaywall = useReleaseInfoPaywall({ setFunnelConfig });
  const releaseInfoFunnelConfig = useMemo(
    () => ({ ...funnelConfig, setFunnelConfig }),
    [funnelConfig, setFunnelConfig],
  );
  const { funnelBody, funnelFooter } = useFunnel(releaseInfoFunnelConfig);

  if (funnelBody) {
    return (
      <Box
        bg="rgba(20, 20, 20, 0.97)"
        borderRadius="md"
        boxShadow="0 8px 24px rgba(0, 0, 0, 0.5)"
        maxW="md"
        w="100%"
        maxH="100%"
        overflowY="auto"
        p={4}
        onClick={(e) => e.stopPropagation()}
      >
        {funnelBody}
        {funnelFooter}
      </Box>
    );
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={4}
      color="white"
      p={6}
      textAlign="center"
      bg="rgba(20, 20, 20, 0.95)"
      borderRadius="md"
      boxShadow="0 8px 24px rgba(0, 0, 0, 0.5)"
      maxW="sm"
      w="100%"
      onClick={(e) => e.stopPropagation()}
    >
      <Text fontSize="xl" fontWeight="bold">
        Continue reading {release.comic?.name ?? "this book"}
      </Text>
      <ReleaseInfoActions
        release={release}
        setFunnelConfig={openPaywall}
        contextArea={FunnelContextArea.suggestionFeed}
        eventContextArea={EventContextArea.suggestionFeed}
      />
    </Flex>
  );
}
